import { Component, OnDestroy, OnInit } from '@angular/core';
import { Dictionary } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { AttachmentService } from '../../Attachment/attachment.service';
import { BusyIndicatorService } from '../../Busy-Indicator';
import { FeatureFlagsService } from '../../Common/FeatureFlags/feature-flags-service';
import { PopUpService } from '../../Common/Popup/popup.service';
import { UpgradeNavigationServiceHandler } from '../../Common/Routing/upgrade-navigation-handler.service';
import { Constants, DocumentExceptionActions, EntityTypeIDs } from '../../constants.new';
import { NavigationService } from '../../Layout/navigation.service';
import { ActionViewPersistenceService } from '../../Task/ActionView/Action.View.Persistence.Service.upgrade';
import { ActionViewStateService } from '../../Task/ActionView/actionViewState.service';
import { MessageBoxButtons, MessageBoxService } from '../../UI-Lib/Message-Box/messagebox.service.upgrade';
import { TimerService } from '../../UI-Lib/Utilities';
import { DocumentResultType, ProcessingService } from '../processing.service.upgrade';
import {
    DocumentProcessingExceptionData,
    DocumentProcessingExceptionModalParams,
} from './Modals/documentProcessingExceptionModal.component';

import * as _ from 'lodash';
import { DocumentService } from 'src/app/Documents/document.service';

type Validator = (callback: (isValid: boolean, message: string) => void) => void;

// Client-only preview fields attached to an intake item at runtime by _loadAttachment. They are not part
// of the server-owned Core.IntakeItemInfo contract and must never be persisted back to the server.
type IntakeItemInfoWithPreview = Core.IntakeItemInfo & {
    attachmentUrl?: string;
    fileInfo?: unknown;
    file?: File;
};

@Component({
    selector: 'document-processing',
    templateUrl: './documentProcessing.component.html',
    styleUrls: ['./documentProcessing.component.scss'],
    standalone: false,
})
export class DocumentProcessingComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _upgradeNavigationService: UpgradeNavigationServiceHandler,
        private readonly _messageBoxService: MessageBoxService,
        private readonly _navigationService: NavigationService,
        private readonly _attachmentService: AttachmentService,
        private readonly _documentService: DocumentService,
        private readonly _processingService: ProcessingService,
        private readonly _actionViewPersistenceService: ActionViewPersistenceService,
        private readonly actionViewStateService: ActionViewStateService,
        private readonly _constants: Constants,
        private readonly _popUpService: PopUpService,
        private readonly _timerService: TimerService,
        private readonly _toastr: ToastrService,
        private readonly _busyIndicatorService: BusyIndicatorService,
        private readonly _featureFlagsService: FeatureFlagsService,
    ) {}

    rows: any[];
    searchTimestamp: string;

    currentRow: any;
    currentRowIndex: number;
    selectedEntity: any;
    documentTypeList: any[];
    completeButtonLabel: string;
    entityIsMapped: boolean;
    associatedEntityMissing: boolean;
    associatedEntityChanged: boolean;
    disableEntryButtons: boolean;
    disableSkip: boolean;
    entityDetails: Core.EntityTree;
    revisionInfo: any;
    resultTypes: DocumentResultType[];
    criteria: any;

    initializing = false;
    ProcessingExceptionStatusText: Dictionary<string>;
    showCriteria = true;
    showOverlay = false;
    isDirty = false;
    warnOnEntityReload = false;
    validationMessage: string;
    dismissModalValidationOpen = false;
    saveEntityValidationOpen = false;
    completeValidationOpen = false;
    loadingRevisionData: boolean;
    isQCItem = false;
    editModeEntityPanel = false;
    isPopoutDynamic = false;
    hasIdpFeature = false;

    // The entity edit panel needs to be able to set up a function that the document processing
    // controller can call to get data entry JSON. We're creating the dataEntryJSONHandler object to
    // set that up; when the entity edit panel loads, it should set the retrieveDataEntryJSON property
    // to a function that will get the current dataEntryJSON value.
    dataEntryJSONHandler = {
        retrieveDataEntryJSON: null,
    };

    // Same kind of deal with the validation handler; document processing needs to reach into the
    // entityEditComponent and call its validation function
    validationHandler = {
        validate: null,
    };

    firstEncounterSavedCb = (result: any) => this.onFirstEncounterSaved(result);

    private _completedItemCount: number = 0;
    private _newDocumentTypeID: number;
    private _navigateWarning: string = 'Entity is in process. Are you sure you want to leave?';
    private _ExceptionStatusIDs = {
        RATES_REQUESTED: 5,
    };
    private _cleanParcelData: any;

    warnOnEntityReloadChanged = (newValue: boolean) => {
        this.warnOnEntityReload = newValue;
    };

    async ngOnInit(): Promise<void> {
        this.initializing = true;
        await this._getDocumentTypeList();

        this.currentRow = null;
        this.currentRowIndex = 0;
        this.entityIsMapped = false;
        this.associatedEntityMissing = false;
        this.associatedEntityChanged = false;
        this.selectedEntity = undefined;
        this.entityDetails = undefined;
        this.completeButtonLabel = '';
        this.hasIdpFeature = this._featureFlagsService.featureFlags.enableIdp;
        this.ProcessingExceptionStatusText = this._constants.ProcessingExceptionStatusText;
        this.criteria = this._processingService.getCriteria();
        this.resultTypes = this._processingService.getResultTypes();

        this._newDocumentTypeID = null;
        this.setDirty(false);

        const selectedRows = this._upgradeNavigationService.getQuerystringParam('selectedRows');
        this.rows = selectedRows ? (selectedRows as any) : [];

        const searchTimestamp = this._upgradeNavigationService.getQuerystringParam('searchTimestamp');
        this.searchTimestamp = searchTimestamp ? searchTimestamp : null;

        // Hack for displaying a single task ID by URL
        const busyRef = this._busyIndicatorService.show({ message: 'Loading' });
        const taskId = this._upgradeNavigationService.getQuerystringParam('taskID');
        const intakeItemId = this._upgradeNavigationService.getQuerystringParam('intakeItemID');
        if (taskId) {
            await this._initializeByTaskID(+taskId);
            await this._initializeRows();
            await busyRef.hide();
        } else if (intakeItemId) {
            await this._initializeByIntakeItemID(+intakeItemId);
            await this._initializeRows();
            await busyRef.hide();
        } else {
            await this._initializeRows();
            await busyRef.hide();
            this._logPinTip(this.rows[0]);
        }

        // $scope.$on('angular-resizable.resizeStart', () => {
        //     this.showOverlay = true;
        // });
        //
        // $scope.$on('angular-resizable.resizeEnd', () => {
        //     this.showOverlay = false;
        // });

        // If the attachment dialog is opened, the popout preview will show attachments potentially; when the modal
        // is closed, navigate it back to the current processing document if appropriate
        // const attachmentClosingEvent = $rootScope.$on('attachment.modal.closing', () => {
        //     const globalAttachmentsPopUpRef = this._popUpService.getByAliasGroup(this._popUpService.StaticAliases.Attachment)[0];
        //     if (globalAttachmentsPopUpRef && this.currentRow && this.currentRow.intakeDocumentInfo) {
        //         this._notifyChildWindow(globalAttachmentsPopUpRef, this.currentRow.intakeDocumentInfo);
        //     }
        // });
        this.initializing = false;
    }

    ngOnDestroy(): void {
        this._navigationService.disableNavWarning();
        // this.attachmentClosingEvent();
    }

    async searchResultSelected(result): Promise<void> {
        this.setDirty(true);
        await this._getEntityTree(result.entityTypeId, result.entityId);
    }

    async forceReloadEntity(includeParcel: boolean): Promise<void> {
        await this._reloadEntity(includeParcel, true);
    }

    async onFirstEncounterSaved(result: any): Promise<void> {
        this.setDirty(true);
        if (!result.updateVersion) {
            await this._reloadEntity(false);
        }
    }

    setDirty = (isDirty: boolean): void => {
        if (this.isDirty !== isDirty) {
            this.isDirty = isDirty;
            if (isDirty) {
                this._navigationService.enableNavWarning(this._navigateWarning);
            } else {
                this._navigationService.disableNavWarning();
            }
        }
    };

    loadQuickSearch(): void {
        this.entityIsMapped = false;
        this.associatedEntityMissing = false;
        this.associatedEntityChanged = false;
        this.showCriteria = true;
        this.setDirty(true);

        this.entityDetails = undefined;
        this.selectedEntity = undefined;
        this.currentRow.intakeDocumentInfo.intakeItem.entryJSON = null;
    }

    ////////////////

    async onSkipClick(): Promise<void> {
        if (this.isDirty) {
            try {
                await this._messageBoxService.confirm(this._navigateWarning);
            } catch (error) {
                return;
            }
        }
        this.disableSkip = true;
        try {
            await this.openNextDocument();
        } finally {
            this.disableSkip = false;
        }
    }

    async openNextDocument(): Promise<any> {
        this.setDirty(false);
        this._newDocumentTypeID = null;
        if (this.currentRowIndex >= this.rows.length - 1) {
            await this._messageBoxService.open({
                title: 'SUCCESS',
                message: `You have completed or skipped all documents. Number of documents processed: ${this._completedItemCount}. Click ok to return`,
                buttons: MessageBoxButtons.OK,
            });
            this._navigationService.disableNavWarning();
            const parcelId = this._upgradeNavigationService.getQuerystringParam('parcelID');
            if (parcelId) {
                this._upgradeNavigationService.go('parcelRedirect', { entityId: parcelId });
            } else {
                const route = this.actionViewStateService.getReturnRoute(
                    this._actionViewPersistenceService.routeParams ?? {},
                );
                this._upgradeNavigationService.go(route.state, route.params);
            }
            return;
        }

        const nextRowIndex = this.currentRowIndex + 1;

        // The next document is prefetched while the user works the current document. IDP can update
        // the intake item, including its RowVersion, after that prefetch. Refresh the complete server
        // model before promoting the row so the UI never combines fresh status with stale intake data.
        if (this.rows[nextRowIndex].intakeDocumentInfo) {
            await this._refreshPrefetchedDocumentInfo(nextRowIndex);
        } else {
            await this._getRowData(nextRowIndex, false);
        }

        // Do not advance navigation state until the refresh succeeds. If the request fails, the user
        // remains on the current document and can retry without partially initialized screen state.
        this.currentRowIndex = nextRowIndex;
        this.selectedEntity = null;
        this.currentRow = this.rows[this.currentRowIndex];

        const globalAttachmentsPopUpRef = this._popUpService.getByAliasGroup(
            this._popUpService.StaticAliases.Attachment,
        )[0];
        globalAttachmentsPopUpRef &&
            this.isPopoutDynamic &&
            this._notifyChildWindow(globalAttachmentsPopUpRef, this.currentRow.intakeDocumentInfo);

        this.entityIsMapped = this.currentRow.intakeDocumentInfo.intakeItem.entityID !== null;
        this.associatedEntityMissing = this.currentRow.intakeDocumentInfo.associatedEntityMissing;
        this.associatedEntityChanged = this.currentRow.intakeDocumentInfo.associatedEntityChanged;
        this.documentTypeChanged();

        if (this.entityIsMapped) {
            if (!this.associatedEntityMissing) {
                await this._getEntityTree(
                    this.currentRow.intakeDocumentInfo.intakeItem.entityTypeID,
                    this.currentRow.intakeDocumentInfo.intakeItem.entityID,
                );
            }
            this.showCriteria = false;
        } else {
            this.entityDetails = undefined;
            this.showCriteria = true;
        }

        this._processingService.changeProcessingDocument();

        // load next one
        return this._getRowData(this.currentRowIndex + 1, false).then(() => {
            const intakeDocumentInfo = this.rows[this.currentRowIndex]?.intakeDocumentInfo;

            // If it has OCR values, map them to the search criteria
            this._maybeMapOCRValues(intakeDocumentInfo);
        });
    }

    async popoutPreview(isDynamic: boolean): Promise<void> {
        const globalAttachmentsPopUpRef = this._popUpService.getByAliasGroup(
            this._popUpService.StaticAliases.Attachment,
        )[0];
        if (globalAttachmentsPopUpRef && globalAttachmentsPopUpRef.isOpen()) {
            this._notifyChildWindow(globalAttachmentsPopUpRef, this.rows[this.currentRowIndex].intakeDocumentInfo);
        } else {
            const popUpRef = await this._popUpService.openAttachmentPreview(
                this._popUpService.StaticAliases.Attachment,
                globalAttachmentsPopUpRef,
            );
            this._notifyChildWindow(popUpRef, this.rows[this.currentRowIndex].intakeDocumentInfo);
        }

        this.isPopoutDynamic = isDynamic;
    }

    openDismissModal(): void {
        this._checkValidationFunction(this.validationHandler.validate)(async (isValid: boolean, message: string) => {
            this.dismissModalValidationOpen = false;
            this.saveEntityValidationOpen = false;
            this.completeValidationOpen = false;

            if (!isValid) {
                this.validationMessage = message;
                // HACK: Need to set timeout for message to propogate before triggering tooltip show
                this._timerService.setTimeout(() => {
                    this.dismissModalValidationOpen = true;
                }, 1);
                return;
            }
            this.validationMessage = null;
            //console.log(this.currentRow);
            let selectedEntity = this.selectedEntity;

            if (!selectedEntity) {
                selectedEntity = {
                    entityID: this.currentRow.intakeDocumentInfo.intakeItem.entityID,
                    entityTypeID: this.currentRow.intakeDocumentInfo.intakeItem.entityTypeID,
                };
            }

            let dataEntryJSON = null;
            if (this.selectedEntity && this.dataEntryJSONHandler && this.dataEntryJSONHandler.retrieveDataEntryJSON) {
                dataEntryJSON = this.dataEntryJSONHandler.retrieveDataEntryJSON();
            }

            const exceptionData: DocumentProcessingExceptionData = {
                selectedRow: this.currentRow,
                selectedEntity: selectedEntity,
                dataEntryJSON: dataEntryJSON,
                newDocumentTypeID: this._newDocumentTypeID,
                entityDetails: this.entityDetails as any,
                taskIDs: null,
            };

            const modalData: DocumentProcessingExceptionModalParams = {
                exceptionData: exceptionData,
                searchTimestamp: new Date(+this.searchTimestamp),
                optionalArgs: { isTaxBill: this.currentRow.f9003 === 'Tax Bill', parentCallsService: false },
                instanceId: this.currentRow.intakeDocumentInfo.intakeBatch.instanceId,
            };

            const modalResult = await this._processingService.launchDocumentProcessingExceptionsModal(modalData);

            if (!modalResult) {
                return;
            }

            this.currentRow.intakeDocumentInfo.intakeItem.rowVersion = modalResult.result.intakeItemRowVersion;

            if (modalResult.actionType === DocumentExceptionActions.CLEAR_EXCEPTION) {
                this.currentRow.intakeDocumentInfo.intakeItem.exceptionStatusID = undefined;
            } else {
                if (modalResult.actionType === DocumentExceptionActions.REQUEST_TAX_RATE_SETUPS) {
                    this.currentRow.intakeDocumentInfo.intakeItem.exceptionStatusID =
                        this._ExceptionStatusIDs.RATES_REQUESTED;
                }
                await this.openNextDocument();
            }
        });
    }

    downloadAttachment() {
        const docInfo = this.currentRow.intakeDocumentInfo;
        this._attachmentService.downloadAttachmentByGuid(
            docInfo.attachmentID,
            docInfo.attachmentMetaData,
            docInfo.fileExtension,
        );
    }

    async saveEntityDetails(): Promise<void> {
        this._checkValidationFunction(this.validationHandler.validate)(async (isValid: boolean, message: string) => {
            this.dismissModalValidationOpen = false;
            this.saveEntityValidationOpen = false;
            this.completeValidationOpen = false;

            if (!isValid) {
                this.validationMessage = message;
                // HACK: Need to set timeout for message to propogate before triggering tooltip show
                this._timerService.setTimeout(() => {
                    this.saveEntityValidationOpen = true;
                }, 1);
                return;
            }
            this.validationMessage = null;
            const documentIntakeID = this.currentRow.intakeDocumentInfo.intakeItem.intakeItemID;
            const taskID = this.currentRow.n10908;
            let dataEntryJSON = null;
            if (this.selectedEntity && this.dataEntryJSONHandler && this.dataEntryJSONHandler.retrieveDataEntryJSON) {
                dataEntryJSON = this.dataEntryJSONHandler.retrieveDataEntryJSON();
            }

            this._checkForChangesInParcel();

            const result = await this._processingService.saveItemEntity(
                documentIntakeID,
                taskID,
                this.selectedEntity,
                this._newDocumentTypeID,
                dataEntryJSON,
                this.currentRow.intakeDocumentInfo.intakeItem.rowVersion,
                this.currentRow.intakeDocumentInfo.parcelChanges,
                this.currentRow.intakeDocumentInfo.intakeItem.extraInfoJSON,
            );

            this.currentRow.intakeDocumentInfo.intakeItem.rowVersion = result.intakeItemRowVersion;
            // The user may have navigated away after clicking save; we don't want to change
            // the dirty state in that case
            if (taskID === this.currentRow.n10908) {
                this.setDirty(false);
            }
            this._toastr.success('Save successful', 'Entity Details have been saved');
        });
    }

    async completeTask(): Promise<void> {
        this.validationHandler.validate(async (isValid: boolean, message: string) => {
            this.dismissModalValidationOpen = false;
            this.saveEntityValidationOpen = false;
            this.completeValidationOpen = false;

            if (!isValid) {
                this.validationMessage = message;
                // HACK: Need to set timeout for message to propagate before triggering tooltip show
                this._timerService.setTimeout(() => {
                    this.completeValidationOpen = true;
                }, 1);
                return;
            }
            this.validationMessage = null;
            const documentIntakeID = this.currentRow.intakeDocumentInfo.intakeItem.intakeItemID;
            const taskID = this.currentRow.n10908;
            let selectedEntity = this.selectedEntity;

            if (!selectedEntity) {
                selectedEntity = {
                    entityID: this.currentRow.intakeDocumentInfo.intakeItem.entityID,
                    entityTypeID: this.currentRow.intakeDocumentInfo.intakeItem.entityTypeID,
                };
            }

            let dataEntryJSON = null;
            if (this.selectedEntity && this.dataEntryJSONHandler && this.dataEntryJSONHandler.retrieveDataEntryJSON) {
                dataEntryJSON = this.dataEntryJSONHandler.retrieveDataEntryJSON();
            }

            this._checkForChangesInParcel();

            this.disableEntryButtons = true;
            try {
                await this._processingService.completeItem(
                    documentIntakeID,
                    taskID,
                    selectedEntity,
                    this._newDocumentTypeID,
                    dataEntryJSON,
                    this.currentRow.intakeDocumentInfo.intakeItem.rowVersion,
                    this.currentRow.intakeDocumentInfo.parcelChanges,
                    this.currentRow.intakeDocumentInfo.intakeItem.extraInfoJSON,
                );

                this._toastr.success('Task completed successfully', 'Success');
                this._completedItemCount++;
                await this.openNextDocument();
            } finally {
                this.disableEntryButtons = false;
            }
        }, true);
    }

    documentTypeChanged(value?: number): void {
        if (value !== undefined) {
            this.currentRow.intakeDocumentInfo.attachmentType = _.find(this.documentTypeList, {
                attachmentTypeID: value,
            });
        }

        this._newDocumentTypeID = this.currentRow.intakeDocumentInfo.attachmentType.attachmentTypeID;
        const documentType = _.find(this.documentTypeList, { attachmentTypeID: this._newDocumentTypeID });

        this.resultTypes = this._processingService.getResultTypesByDocumentType(documentType);

        if (!_.some(this.resultTypes, { type: this.criteria.resultType }) && this.resultTypes.length) {
            const foundResultType = _.find(this.resultTypes, { id: documentType.defaultResultsEntityTypeID });

            this.criteria.resultType = foundResultType ? foundResultType.type : this.resultTypes[0].type;
        }
    }

    getCommentsModalData = () => {
        return {
            entityTypeID: EntityTypeIDs.INTAKE_ITEM,
            entityID: this.currentRow.intakeDocumentInfo.intakeItem.intakeItemID,
            description: this.getIntakeBatchItemNumber(),
            canEdit: true,
        };
    };

    getIntakeBatchItemNumber(): string {
        return `Intake Item: ${this.currentRow.intakeDocumentInfo.intakeBatch.batchNumber}#${
            this.currentRow.intakeDocumentInfo.intakeItem.itemNumber
        }${this.currentRow.intakeDocumentInfo.intakeItem.duplicateNumber ? `_${this.currentRow.intakeDocumentInfo.intakeItem.duplicateNumber}` : ''}`;
    }

    async launchDuplicateModal(): Promise<void> {
        const duplicateItem = {
            duplicateCount: 1,
            intakeItemID: this.currentRow.intakeDocumentInfo.intakeItem.intakeItemID,
            attachmentTypeID: this.currentRow.intakeDocumentInfo.attachmentType.attachmentTypeID,
        };
        try {
            const result = await this._processingService.launchDuplicateIntakeItemModal(duplicateItem);
            if (!result) {
                return;
            }

            const newItems = await result();

            if (newItems) {
                this._toastr.success('Duplicate successfully created');

                // WK-5083 Don't show duplicate items if we're currently on a QC task
                if (!this.isQCItem) {
                    let newIndex = this.currentRowIndex + 1;
                    newItems.forEach((item) => {
                        this.rows.splice(newIndex, 0, item);
                        newIndex++;
                    });

                    await this._getRowData(this.currentRowIndex + 1);
                }
            }
        } catch (error) {
            this._toastr.error('Failed to duplicate, please try again');
        }
    }

    billRevisionChanged(revision): void {
        let fmv = new Decimal(0);
        let av = new Decimal(0);
        revision.annualGridComponents.forEach((component) => {
            if (component.assessmentComponentTypeID !== 3) {
                fmv = fmv.plus(component.fairMarketValue);
                av = av.plus(component.assessedValue);
            }
        });
        this.revisionInfo = {
            totalFMV: fmv.toNumber(),
            totalAssessedValue: av.toNumber(),
            isProjected: revision.status !== 1,
        };

        this.loadingRevisionData = false;
    }

    isOverridden(field: string, isAddress?: boolean) {
        if (!this.currentRow.intakeDocumentInfo.parcelChanges) {
            return false;
        }

        if (field === 'taxSetup') {
            return !!this.currentRow.intakeDocumentInfo.parcelChanges.newTaxSetup;
        } else if (isAddress) {
            return !_.isEqual(
                this.currentRow.intakeDocumentInfo.parcelChanges.parcelAddress[field],
                this.entityDetails.extendedData.parcelAddress[field],
            );
        } else {
            return !_.isEqual(
                this.currentRow.intakeDocumentInfo.parcelChanges[field],
                this.entityDetails.extendedData[field],
            );
        }
    }

    private _checkValidationFunction(validator: Validator): Validator {
        // wk-2511- Dismiss and save buttons not working until an entity has been selected
        if (validator) {
            return validator;
        } else {
            return (callback: (isValid: boolean, message: string) => void) => {
                callback(true, '');
            };
        }
    }

    private _logPinTip(row): void {
        if (row && row.n10908) {
            console.log(
                'To pin this task to document processing, paste the following at the end of your url and refresh the page:\n' +
                    `?taskID=${row.n10908}`,
            );
        }
    }

    private _notifyChildWindow(popUpRef, intakeDocumentInfo) {
        this._logPinTip(this.currentRow);
        if (popUpRef.isOpen()) {
            const popUpData = {
                attachmentInfo: {
                    attachmentID: intakeDocumentInfo.attachmentID,
                    fileName: intakeDocumentInfo.attachmentMetaData,
                    fileExtension: intakeDocumentInfo.fileExtension,
                },
            };
            popUpRef.publish(this._popUpService.Topics.Attachment.AttachmentChanged, popUpData);
        }
    }

    private async _getEntityTree(
        entityTypeId: number,
        entityId: number,
        forceReload?: boolean,
    ): Promise<Core.EntityTree> {
        this.selectedEntity = {
            entityId: entityId,
            entityTypeId: entityTypeId,
            forceReload,
        };

        this.disableEntryButtons = true;
        try {
            this.entityDetails = await this._processingService.getEntityTree(entityTypeId, entityId, true);

            if (this.entityDetails.extendedData.propertyTypeID) {
                (this.entityDetails.extendedData as any).propertyTypeAbbr = _.find(this._constants.PropertyTypes, {
                    propertyTypeID: this.entityDetails.extendedData.propertyTypeID,
                }).propTypeAbbr;
            }

            this.entityIsMapped = true;
            this.associatedEntityMissing = false;

            this._cleanParcelData = null;
            this.editModeEntityPanel = false;
        } finally {
            this.disableEntryButtons = false;
        }

        return this.entityDetails;
    }

    private async _initializeRows(): Promise<void> {
        if (this.rows.length > 0) {
            await this._getRowData(0, true);
            // refresh pop-up window if it is available
            if (this.rows[this.currentRowIndex] && this.rows[this.currentRowIndex].intakeDocumentInfo) {
                const globalAttachmentsPopUpRef = this._popUpService.getByAliasGroup(
                    this._popUpService.StaticAliases.Attachment,
                )[0];
                if (globalAttachmentsPopUpRef) {
                    this._notifyChildWindow(
                        globalAttachmentsPopUpRef,
                        this.rows[this.currentRowIndex].intakeDocumentInfo,
                    );
                }
            }

            switch (this.rows[0].n10902) {
                case 31:
                    this.completeButtonLabel = 'Complete Data Entry';
                    this.isQCItem = false;
                    break;
                case 32:
                    this.completeButtonLabel = 'Complete QC';
                    this.isQCItem = true;
                    break;
            }

            const intakeDocumentInfo = this.rows[0].intakeDocumentInfo;

            // If it has OCR values, map them to the search criteria
            this._maybeMapOCRValues(intakeDocumentInfo);

            this.entityIsMapped = intakeDocumentInfo.intakeItem.entityID !== null;
            if (this.entityIsMapped) {
                this.associatedEntityMissing = intakeDocumentInfo.associatedEntityMissing;
                this.associatedEntityChanged = intakeDocumentInfo.associatedEntityChanged;
                const entityId = intakeDocumentInfo.intakeItem.entityID;
                const entityTypeId = intakeDocumentInfo.intakeItem.entityTypeID;
                this.documentTypeChanged();

                if (!this.associatedEntityMissing) {
                    await this._getEntityTree(entityTypeId, entityId);
                }
            }
        }
    }

    private _maybeMapOCRValues(intakeDocumentInfo): void {
        if (!intakeDocumentInfo) return;

        const { taxBillOcrResults, assessmentNoticeOcrResults, idpConfidence, associatedEntityMissing, idpStatusID } =
            intakeDocumentInfo;

        if (taxBillOcrResults) {
            _.assign(
                this.criteria,
                this._processingService.getCriteriaWithIdp(taxBillOcrResults, idpConfidence, associatedEntityMissing),
            );
        } else if (assessmentNoticeOcrResults) {
            _.assign(
                this.criteria,
                this._processingService.getCriteriaWithIdp(
                    assessmentNoticeOcrResults,
                    idpConfidence,
                    associatedEntityMissing,
                ),
            );
        }

        if (taxBillOcrResults || assessmentNoticeOcrResults || idpConfidence) {
            this.criteria.anticipated = true;
            this.criteria.primaryAcctNumOnly = false;
        }

        if (idpStatusID && !taxBillOcrResults && !assessmentNoticeOcrResults) {
            this.criteria['isIdpSearch'] = true;
        }
    }

    private async _initializeByTaskID(taskID: number): Promise<void> {
        const taskInfo = await this._documentService.getTaskInfoByTaskID(taskID);
        this.rows = [taskInfo];
    }

    private async _initializeByIntakeItemID(intakeItemID: number): Promise<void> {
        const taskInfo = await this._documentService.getTaskInfo(intakeItemID);
        this.rows = [taskInfo];
    }

    private async _getRowData(rowIndex: number, displayNow?: boolean): Promise<void> {
        if (!(this.rows.length <= rowIndex)) {
            //TODO: Maybe check for the intakeDocumentInfo object first
            this.rows[rowIndex].intakeDocumentInfo = await this._documentService.getDocumentInfoByTaskId(
                this.rows[rowIndex].n10908 || this.rows[rowIndex].taskID,
            ); //https://dev.azure.com/ryanunity/PropertyPoint/_workitems/edit/302375
            this.rows[rowIndex].fileError = false;
            await this._loadAttachment(rowIndex, displayNow);
        }
    }

    private async _refreshPrefetchedDocumentInfo(rowIndex: number): Promise<void> {
        const row = this.rows[rowIndex];
        const prefetchedDocumentInfo = row.intakeDocumentInfo as IntakeItemInfoWithPreview;
        const refreshedDocumentInfo = (await this._documentService.getDocumentInfoByTaskId(
            row.n10908 || row.taskID,
        )) as IntakeItemInfoWithPreview;

        // Reuse the already-created browser preview only when the refreshed model references the same
        // attachment AND that preview actually loaded during prefetch. Replace every server-owned field
        // (especially IDP results and IntakeItem.RowVersion) from the refreshed response.
        const canReusePreview =
            refreshedDocumentInfo.attachmentID === prefetchedDocumentInfo.attachmentID &&
            !row.fileError &&
            !!prefetchedDocumentInfo.attachmentUrl;
        if (canReusePreview) {
            refreshedDocumentInfo.attachmentUrl = prefetchedDocumentInfo.attachmentUrl;
            refreshedDocumentInfo.fileInfo = prefetchedDocumentInfo.fileInfo;
            refreshedDocumentInfo.file = prefetchedDocumentInfo.file;
            row.intakeDocumentInfo = refreshedDocumentInfo;
            return;
        }

        // Either the attachment identity changed or the prefetched preview never loaded (e.g. a transient
        // download failure). Discard the stale preview, clear the prior file error, and load the binary
        // that matches the refreshed server model so the promoted document is displayable.
        row.intakeDocumentInfo = refreshedDocumentInfo;
        row.fileError = false;
        await this._loadAttachment(rowIndex, false);
    }

    private async _loadAttachment(rowIndex: number, displayNow: boolean): Promise<void> {
        const fileInfo = this._attachmentService.getMimeTypeByFileExtension(
            this.rows[rowIndex].intakeDocumentInfo.fileExtension,
        );
        if (fileInfo.previewSupported) {
            try {
                const response = await this._documentService.getDocumentBody(
                    this.rows[rowIndex].intakeDocumentInfo.attachmentID,
                );
                const attachmentBlob = new Blob([response.body], { type: fileInfo.type });
                const attachmentBlobUrl = URL.createObjectURL(attachmentBlob);
                //var fileInfo = attachmentService.getMimeTypeByFileExtension(fileExtension);

                if (fileInfo.previewSupported) {
                    this.rows[rowIndex].intakeDocumentInfo.attachmentUrl = attachmentBlobUrl;
                    this.rows[rowIndex].intakeDocumentInfo.fileInfo = fileInfo;
                    this.rows[rowIndex].intakeDocumentInfo.file = new File(
                        [attachmentBlob],
                        this.rows[rowIndex].intakeDocumentInfo.fileName,
                        { type: fileInfo.type },
                    );
                }

                if (displayNow == true) {
                    this.currentRow = this.rows[rowIndex];
                }

                // we might want to load next row - if there is one
                //
                if (rowIndex == 0 && this.rows.length >= 2) {
                    if (!this.rows[1].intakeDocumentInfo) {
                        await this._getRowData(1, false);
                    }
                }
            } catch (err) {
                this.rows[rowIndex].fileError = true;
            } finally {
                if (displayNow == true) {
                    this.currentRow = this.rows[rowIndex];
                }
            }
        } else {
            this.rows[rowIndex].intakeDocumentInfo.attachmentUrl = null;
            this.rows[rowIndex].intakeDocumentInfo.fileInfo = fileInfo;
            if (displayNow == true) {
                this.currentRow = this.rows[rowIndex];
            }

            // we might want to load next row - if there is one
            //
            if (rowIndex == 0 && this.rows.length >= 2) {
                // console.log('getRowData - loading second row right away ');
                if (!this.rows[1].intakeDocumentInfo) {
                    await this._getRowData(1, false);
                }
            }
        }
    }

    private async _getDocumentTypeList(): Promise<void> {
        this.documentTypeList = await this._attachmentService.getAttachmentTypes();
    }

    private _checkForChangesInParcel(): void {
        if (
            !this.isOverridden('assesseeName') &&
            !this.isOverridden('address1', true) &&
            !this.isOverridden('address2', true) &&
            !this.isOverridden('city', true) &&
            !this.isOverridden('zip', true) &&
            !this.isOverridden('collectorAcctNum') &&
            !this.isOverridden('taxSetup')
        ) {
            this.currentRow.intakeDocumentInfo.parcelChanges = null;
        }
    }

    private async _reloadEntity(includeParcel: boolean, forceReload?: boolean): Promise<void> {
        this.loadingRevisionData = true;

        // Reset the entity edit panel first, then reload the necessary data
        const originalSelectedEntity = this.selectedEntity;
        this.selectedEntity = null;
        if (includeParcel) {
            this.currentRow.intakeDocumentInfo.parcelChanges = null;
        }
        const originalDataEntryObject = JSON.parse(this.dataEntryJSONHandler.retrieveDataEntryJSON());
        originalDataEntryObject.resetOnLoad = true;
        this.currentRow.intakeDocumentInfo.intakeItem.entryJSON = JSON.stringify(originalDataEntryObject);
        const wasOriginallyChanged = this.associatedEntityChanged;
        this.editModeEntityPanel = false;

        this._timerService.setTimeout(async () => {
            this.associatedEntityChanged = false;
            if (wasOriginallyChanged) {
                this.setDirty(true);
            }
            if (includeParcel) {
                await this._getEntityTree(
                    originalSelectedEntity.entityTypeId,
                    originalSelectedEntity.entityId,
                    forceReload,
                );
            } else {
                this.selectedEntity = {
                    entityId: originalSelectedEntity.entityId,
                    entityTypeId: originalSelectedEntity.entityTypeId,
                    forceReload,
                };
            }
        }, 1);
    }
}
